/**
 * GenViz / SmartFence — Schémas de validation partagés (zod).
 *
 * Ce fichier est la traduction EXÉCUTABLE des règles de saisie de la spec (§3.4.1.2, §3.4.2.2,
 * §3.4.3.2, §7.2.2) et des bornes du référentiel de paramètres. Il est utilisé :
 *  - par le backend NestJS, comme pipe de validation d'entrée (source de vérité) ;
 *  - par l'application mobile, pour valider avant l'appel réseau et activer/désactiver le bouton ;
 *  - par le back-office, pour ses formulaires internes.
 *
 * Les messages d'erreur des schémas sont des CODES du catalogue `errors.ts`, jamais du texte
 * destiné à l'utilisateur : la traduction reste à la charge de l'application.
 *
 * Jeux de caractères : les expressions `*_SPEC` reproduisent LITTÉRALEMENT la spec. Elles sont
 * volontairement étroites (ni apostrophe, ni majuscule accentuée, ni ï/ô/â) et bloquent des noms
 * légitimes comme « L'Hermitage » ou « Éric ». Les expressions `*_ELARGI` correspondent à la
 * recommandation de la revue critique (`\p{L}\p{N}` + ponctuation usuelle, normalisation NFC) ;
 * le basculement est un arbitrage client, matérialisé par `CHARSET_ELARGI_ACTIF`.
 */

import { z } from 'zod';

import { CodeErreur } from './errors.js';
import {
  CauseEchecCommande,
  CriticiteMinimaleNotification,
  EtatAcquittement,
  EtatAlimentation,
  FREQUENCES_GEOLOC_MIN,
  Langue,
  ModePositionsDemande,
  OrigineNumeroSerie,
  PeriodeHistorique,
  PlateformePush,
  PorteeListeAlertes,
  TriListeAppareils,
  TypeCommande,
  TypeEvenementObjet,
  TypeMessageMontant,
} from './enums.js';

/* -------------------------------------------------------------------------- */
/* 1. Longueurs et bornes                                                      */
/* -------------------------------------------------------------------------- */

/** Longueurs maximales imposées par la spec, en POINTS DE CODE Unicode. */
export const LONGUEUR_MAX = {
  NOM_EXPLOITATION: 50,
  PRENOM: 30,
  NOM: 30,
  EMAIL: 254,
  /** Partie locale d'une adresse e-mail (RFC 5321). */
  EMAIL_PARTIE_LOCALE: 64,
  MOT_DE_PASSE: 64,
  NUMERO_SERIE: 50,
  NOM_ELECTRIFICATEUR: 50,
  CLE_APPAIRAGE: 8,
  CODE_INVITATION: 12,
  MOTIF: 200,
  SUJET_TICKET: 120,
  MESSAGE_TICKET: 4000,
  JETON: 512,
  JETON_FCM: 255,
  FUSEAU_HORAIRE: 64,
  FIRMWARE_VERSION: 20,
} as const;

/** Longueurs minimales. */
export const LONGUEUR_MIN = {
  MOT_DE_PASSE: 8,
} as const;

/** Bornes numériques du référentiel (valeurs usine par défaut, surchargées par `/v1/parameters`). */
export const BORNES = {
  TENSION_MIN_KV: 0,
  TENSION_MAX_KV: 15,
  GARDIENNAGE_MIN_KV: 1,
  GARDIENNAGE_MAX_KV: 12,
  GARDIENNAGE_PAS_KV: 0.1,
  POURCENTAGE_ZONE_ORANGE: 20,
  NB_MAX_POINTS_GRAPHE: 200,
  NB_POINTS_GEOLOC: 20,
  NB_POINTS_PARCOURS_AFFICHES: 10,
  LIMITE_PAGE_MIN: 1,
  LIMITE_PAGE_MAX: 100,
  LIMITE_PAGE_DEFAUT: 20,
  DUREE_SESSION_MESURE_MIN_S: 10,
  DUREE_SESSION_MESURE_MAX_S: 600,
  DUREE_SESSION_MESURE_DEFAUT_S: 120,
} as const;

/* -------------------------------------------------------------------------- */
/* 2. Jeux de caractères                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Bascule d'arbitrage : `false` applique le charset LITTÉRAL de la spec, `true` applique le
 * charset élargi recommandé par la revue critique. Tant que le client n'a pas tranché, la
 * spec fait foi.
 */
export const CHARSET_ELARGI_ACTIF = false;

/** Nom d'exploitation — spec : alphanumérique + espace + `& é è ç à - _ * µ ~`. */
export const RE_NOM_EXPLOITATION_SPEC = /^[A-Za-z0-9 &éèçà\-_*µ~]+$/u;

/** Nom d'exploitation — variante élargie recommandée (lettres Unicode, apostrophe, tiret). */
export const RE_NOM_EXPLOITATION_ELARGI = /^[\p{L}\p{N} '’&\-_*µ~.]+$/u;

/** Prénom et nom — spec : alphanumérique + espace + `é è ç à ~`. */
export const RE_PRENOM_NOM_SPEC = /^[A-Za-z0-9 éèçà~]+$/u;

/** Prénom et nom — variante élargie recommandée. */
export const RE_PRENOM_NOM_ELARGI = /^[\p{L}\p{N} '’\-~.]+$/u;

/** Numéro de série — spec : alphanumérique + `-`, `_` et espaces. */
export const RE_NUMERO_SERIE = /^[A-Za-z0-9 \-_]+$/u;

/**
 * Nom d'électrificateur — spec : alphanumérique + `! € $ £ ~ & é è ê ù * µ`.
 * HYPOTHÈSE : l'espace est autorisé (la spec ne le cite pas mais « Clôture Nord » est le cas
 * d'usage évident, et l'écran 01C propose une saisie libre).
 */
export const RE_NOM_ELECTRIFICATEUR_SPEC = /^[A-Za-z0-9 !€$£~&éèêù*µ]+$/u;

/** Nom d'électrificateur — variante élargie recommandée. */
export const RE_NOM_ELECTRIFICATEUR_ELARGI = /^[\p{L}\p{N} '’!€$£~&\-_*µ.]+$/u;

/**
 * Adresse e-mail — lecture pragmatique de RFC 5321/5322 : une partie locale sans guillemets,
 * un `@`, un domaine en étiquettes valides, une extension de 2 à 63 lettres.
 */
export const RE_EMAIL =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

/** Code d'invitation normalisé : 12 caractères Base32 Crockford (sans I, L, O, U). */
export const RE_CODE_INVITATION = /^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{12}$/;

/** Clé d'appairage du QR code : 8 caractères Base32 Crockford. */
export const RE_CLE_APPAIRAGE = /^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{8}$/;

/** Identifiant de fuseau horaire IANA (`Europe/Paris`, `America/Argentina/Salta`, `UTC`). */
export const RE_FUSEAU_HORAIRE = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/;

/** Horodatage ISO 8601 en UTC, suffixe `Z` obligatoire. */
export const RE_ISO_DATETIME_UTC =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

/** Heure locale `HH:mm` (plage de silence des notifications). */
export const RE_HEURE_HH_MM = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

/** Version d'application, format `1.4.2 (137)` ou `1.4.2`. */
export const RE_VERSION_APP = /^\d+\.\d+\.\d+(?: \(\d+\))?$/;

/** UUID (toute version, forme canonique minuscule ou majuscule). */
export const RE_UUID =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/* -------------------------------------------------------------------------- */
/* 3. Normalisation                                                            */
/* -------------------------------------------------------------------------- */

/** Normalisation Unicode NFC — appliquée AVANT toute mesure de longueur et tout hachage. */
export function normaliserNfc(valeur: string): string {
  return valeur.normalize('NFC');
}

/** Supprime les espaces de tête et de queue, et compacte les espaces internes. */
export function compacterEspaces(valeur: string): string {
  return valeur.trim().replace(/\s+/g, ' ');
}

/** Longueur en POINTS DE CODE (et non en unités UTF-16) : « 😀 » compte pour 1. */
export function longueurPointsDeCode(valeur: string): number {
  return Array.from(valeur).length;
}

/**
 * Normalisation d'une adresse e-mail : NFC, suppression des espaces, passage en minuscules.
 * La minuscule est OBLIGATOIRE (et non « recommandée » comme le dit la spec) : l'e-mail est
 * l'identifiant de connexion, une casse variable rendrait la connexion incohérente.
 */
export function normaliserEmail(valeur: string): string {
  return normaliserNfc(valeur).trim().toLowerCase();
}

/** Normalisation d'un nom saisi : NFC + compactage des espaces. */
export function normaliserTexteSaisi(valeur: string): string {
  return compacterEspaces(normaliserNfc(valeur));
}

/** Normalisation d'un numéro de série : NFC, majuscules, espaces compactés. */
export function normaliserNumeroSerie(valeur: string): string {
  return compacterEspaces(normaliserNfc(valeur)).toUpperCase();
}

/**
 * Normalisation d'un code d'invitation : majuscules, suppression des tirets et des espaces,
 * tolérance Crockford (`O` → `0`, `I`/`L` → `1`). Les tirets sont insérés par l'application
 * pour l'affichage `XXXX-XXXX-XXXX` et IGNORÉS par le serveur.
 */
export function normaliserCodeInvitation(valeur: string): string {
  return normaliserNfc(valeur)
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
}

/** Découpe un code normalisé en blocs de 4 pour l'affichage `XXXX-XXXX-XXXX`. */
export function formaterCodeInvitation(codeNormalise: string): string {
  return (codeNormalise.match(/.{1,4}/g) ?? []).join('-');
}

/** Retire les diacritiques (comparaison d'unicité insensible aux accents). */
export function sansDiacritiques(valeur: string): string {
  return valeur.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Clé de comparaison d'un nom d'exploitation ou d'électrificateur :
 * NFC → compactage → minuscules → suppression des diacritiques.
 */
export function cleComparaison(valeur: string): string {
  return sansDiacritiques(normaliserTexteSaisi(valeur).toLowerCase());
}

/* -------------------------------------------------------------------------- */
/* 4. Contrôles unitaires                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Vrai si la chaîne ne contient QUE des caractères imprimables (espaces compris).
 * Implémentation volontairement sans `\p{…}` : elle doit se comporter à l'identique sur
 * Node 22 et sur Hermes.
 */
export function estImprimable(valeur: string): boolean {
  for (const caractere of valeur) {
    const point = caractere.codePointAt(0);
    if (point === undefined) return false;
    // C0, DEL, C1
    if (point < 0x20 || (point >= 0x7f && point <= 0x9f)) return false;
    // Séparateurs de ligne et de paragraphe
    if (point === 0x2028 || point === 0x2029) return false;
  }
  return true;
}

/** Vrai si l'adresse respecte les longueurs et la syntaxe RFC 5321/5322 retenues. */
export function estEmailValide(valeur: string): boolean {
  if (longueurPointsDeCode(valeur) > LONGUEUR_MAX.EMAIL) return false;
  const arobase = valeur.lastIndexOf('@');
  if (arobase <= 0) return false;
  const partieLocale = valeur.slice(0, arobase);
  if (longueurPointsDeCode(partieLocale) > LONGUEUR_MAX.EMAIL_PARTIE_LOCALE) return false;
  return RE_EMAIL.test(valeur);
}

/**
 * Vrai si l'identifiant de fuseau est reconnu par l'environnement d'exécution.
 * Le contrôle syntaxique seul est insuffisant : `Europe/Pariss` passerait.
 */
export function estFuseauHoraireValide(valeur: string): boolean {
  if (!RE_FUSEAU_HORAIRE.test(valeur)) return false;
  if (longueurPointsDeCode(valeur) > LONGUEUR_MAX.FUSEAU_HORAIRE) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: valeur }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

/** Vrai si la tension respecte le pas de réglage (0,1 kV), à la tolérance flottante près. */
export function respectePasGardiennage(
  tensionKv: number,
  pasKv: number = BORNES.GARDIENNAGE_PAS_KV,
): boolean {
  const rapport = tensionKv / pasKv;
  return Math.abs(rapport - Math.round(rapport)) < 1e-6;
}

/**
 * Calcule `ValeurHauteOrange` — frontière entre la zone orange et la zone verte du cadran.
 * FORMULE CORRIGÉE : `gardiennage + pourcentage × (max − gardiennage)`. La formule de la spec
 * (produit de deux tensions) est dimensionnellement fausse.
 */
export function calculerValeurHauteOrange(
  tensionGardiennageKv: number,
  tensionMaxKv: number = BORNES.TENSION_MAX_KV,
  pourcentageZoneOrange: number = BORNES.POURCENTAGE_ZONE_ORANGE,
): number {
  const valeur =
    tensionGardiennageKv +
    (pourcentageZoneOrange / 100) * (tensionMaxKv - tensionGardiennageKv);
  return Math.round(valeur * 1000) / 1000;
}

/* -------------------------------------------------------------------------- */
/* 5. Robustesse du mot de passe                                               */
/* -------------------------------------------------------------------------- */

/**
 * Règles de robustesse. La spec prévoit l'erreur « Mot de passe trop faible » sans jamais
 * définir la règle : celle-ci est une PROPOSITION à faire valider, conforme aux recommandations
 * ANSSI/NIST (aucune exigence de classes de caractères, qui sont contre-productives).
 */
export const RegleMotDePasse = {
  LONGUEUR_INSUFFISANTE: 'LONGUEUR_INSUFFISANTE',
  LONGUEUR_EXCESSIVE: 'LONGUEUR_EXCESSIVE',
  CARACTERE_NON_IMPRIMABLE: 'CARACTERE_NON_IMPRIMABLE',
  CONTIENT_INFORMATION_PERSONNELLE: 'CONTIENT_INFORMATION_PERSONNELLE',
  TROP_REPETITIF: 'TROP_REPETITIF',
  /** Vérifiée côté serveur uniquement (listes compromises, k-anonymity HIBP). */
  MOT_DE_PASSE_COMPROMIS: 'MOT_DE_PASSE_COMPROMIS',
  /** Vérifiée côté serveur uniquement (score zxcvbn >= 2). */
  SCORE_INSUFFISANT: 'SCORE_INSUFFISANT',
} as const;
export type RegleMotDePasse = (typeof RegleMotDePasse)[keyof typeof RegleMotDePasse];

/** Contexte facultatif : un mot de passe ne doit pas contenir l'identité de son porteur. */
export interface ContexteMotDePasse {
  email?: string;
  prenom?: string;
  nom?: string;
  nom_exploitation?: string;
}

export interface ResultatRobustesse {
  valide: boolean;
  regles_non_respectees: RegleMotDePasse[];
}

/**
 * Contrôles de robustesse exécutables des deux côtés (l'app peut désactiver son bouton sans
 * aller-retour réseau). Les règles serveur seules — listes compromises et score zxcvbn — sont
 * ajoutées par le backend.
 */
export function verifierRobustesseMotDePasse(
  motDePasse: string,
  contexte: ContexteMotDePasse = {},
): ResultatRobustesse {
  const normalise = normaliserNfc(motDePasse);
  const regles: RegleMotDePasse[] = [];
  const longueur = longueurPointsDeCode(normalise);

  if (longueur < LONGUEUR_MIN.MOT_DE_PASSE) {
    regles.push(RegleMotDePasse.LONGUEUR_INSUFFISANTE);
  }
  if (longueur > LONGUEUR_MAX.MOT_DE_PASSE) {
    regles.push(RegleMotDePasse.LONGUEUR_EXCESSIVE);
  }
  if (!estImprimable(normalise)) {
    regles.push(RegleMotDePasse.CARACTERE_NON_IMPRIMABLE);
  }

  const minuscule = normalise.toLowerCase();
  const fragments: string[] = [];
  if (contexte.email !== undefined && contexte.email.length > 0) {
    const local = contexte.email.split('@')[0];
    if (local !== undefined) fragments.push(local.toLowerCase());
  }
  if (contexte.prenom !== undefined) fragments.push(contexte.prenom.toLowerCase());
  if (contexte.nom !== undefined) fragments.push(contexte.nom.toLowerCase());
  if (contexte.nom_exploitation !== undefined) {
    fragments.push(contexte.nom_exploitation.toLowerCase());
  }
  const contientIdentite = fragments.some(
    (fragment) => fragment.length >= 3 && minuscule.includes(sansDiacritiques(fragment)),
  );
  if (contientIdentite) {
    regles.push(RegleMotDePasse.CONTIENT_INFORMATION_PERSONNELLE);
  }

  // Un unique caractère répété, ou une séquence de deux caractères répétée.
  if (/^(.)\1+$/u.test(normalise) || /^(..)\1+$/u.test(normalise)) {
    regles.push(RegleMotDePasse.TROP_REPETITIF);
  }

  return { valide: regles.length === 0, regles_non_respectees: regles };
}

/* -------------------------------------------------------------------------- */
/* 6. Schémas de champ                                                         */
/* -------------------------------------------------------------------------- */

const msg = (code: CodeErreur) => ({ message: code });

/** Champ obligatoire non vide, après compactage des espaces. */
function champObligatoire() {
  return z
    .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
    .transform(normaliserTexteSaisi)
    .refine((v) => v.length > 0, msg(CodeErreur.CHAMP_OBLIGATOIRE));
}

/** Nom d'exploitation : 50 caractères, charset §3.4.2.2. */
export const nomExploitationSchema = champObligatoire()
  .refine(
    (v) => longueurPointsDeCode(v) <= LONGUEUR_MAX.NOM_EXPLOITATION,
    msg(CodeErreur.ERREUR_VALIDATION),
  )
  .refine(
    (v) =>
      (CHARSET_ELARGI_ACTIF ? RE_NOM_EXPLOITATION_ELARGI : RE_NOM_EXPLOITATION_SPEC).test(v),
    msg(CodeErreur.ERREUR_VALIDATION),
  );

/** Prénom : 30 caractères, charset §3.4.2.2. */
export const prenomSchema = champObligatoire()
  .refine((v) => longueurPointsDeCode(v) <= LONGUEUR_MAX.PRENOM, msg(CodeErreur.ERREUR_VALIDATION))
  .refine(
    (v) => (CHARSET_ELARGI_ACTIF ? RE_PRENOM_NOM_ELARGI : RE_PRENOM_NOM_SPEC).test(v),
    msg(CodeErreur.ERREUR_VALIDATION),
  );

/** Nom de famille : 30 caractères, même charset que le prénom. */
export const nomSchema = champObligatoire()
  .refine((v) => longueurPointsDeCode(v) <= LONGUEUR_MAX.NOM, msg(CodeErreur.ERREUR_VALIDATION))
  .refine(
    (v) => (CHARSET_ELARGI_ACTIF ? RE_PRENOM_NOM_ELARGI : RE_PRENOM_NOM_SPEC).test(v),
    msg(CodeErreur.ERREUR_VALIDATION),
  );

/** Adresse e-mail : normalisée en minuscules, 254 caractères, RFC 5321/5322. */
export const emailSchema = z
  .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .transform(normaliserEmail)
  .refine((v) => v.length > 0, msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .refine(estEmailValide, msg(CodeErreur.ADRESSE_EMAIL_INVALIDE));

/**
 * Mot de passe : 8 à 64 points de code, tout Unicode imprimable espaces compris, sensible à la
 * casse. Normalisation NFC AVANT hachage (non spécifiée mais indispensable avec de l'Unicode).
 */
export const motDePasseSchema = z
  .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .transform(normaliserNfc)
  .refine(
    (v) => longueurPointsDeCode(v) >= LONGUEUR_MIN.MOT_DE_PASSE,
    msg(CodeErreur.MOT_DE_PASSE_TROP_FAIBLE),
  )
  .refine(
    (v) => longueurPointsDeCode(v) <= LONGUEUR_MAX.MOT_DE_PASSE,
    msg(CodeErreur.ERREUR_VALIDATION),
  )
  .refine(estImprimable, msg(CodeErreur.ERREUR_VALIDATION));

/** Numéro de série : 50 caractères, alphanumérique + `-`, `_` et espaces, normalisé. */
export const numeroSerieSchema = z
  .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .transform(normaliserNumeroSerie)
  .refine((v) => v.length > 0, msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .refine(
    (v) => longueurPointsDeCode(v) <= LONGUEUR_MAX.NUMERO_SERIE,
    msg(CodeErreur.NUMERO_SERIE_INCORRECT),
  )
  .refine((v) => RE_NUMERO_SERIE.test(v), msg(CodeErreur.NUMERO_SERIE_INCORRECT));

/** Nom d'électrificateur : 50 caractères, charset §3.4.3.2, unicité vérifiée côté serveur. */
export const nomElectrificateurSchema = champObligatoire()
  .refine(
    (v) => longueurPointsDeCode(v) <= LONGUEUR_MAX.NOM_ELECTRIFICATEUR,
    msg(CodeErreur.ERREUR_VALIDATION),
  )
  .refine(
    (v) =>
      (CHARSET_ELARGI_ACTIF
        ? RE_NOM_ELECTRIFICATEUR_ELARGI
        : RE_NOM_ELECTRIFICATEUR_SPEC
      ).test(v),
    msg(CodeErreur.ERREUR_VALIDATION),
  );

/** Code d'invitation : normalisé (tirets, espaces et casse ignorés) puis validé. */
export const codeInvitationSchema = z
  .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .transform(normaliserCodeInvitation)
  .refine((v) => RE_CODE_INVITATION.test(v), msg(CodeErreur.CODE_INVITATION_INVALIDE));

/** Clé d'appairage issue du QR code (`GVZ:1|SN=…|K=…`). */
export const cleAppairageSchema = z
  .string()
  .transform((v) => normaliserNfc(v).trim().toUpperCase())
  .refine((v) => RE_CLE_APPAIRAGE.test(v), msg(CodeErreur.CLE_APPAIRAGE_INVALIDE));

/** Jeton opaque (appairage, invitation, rafraîchissement, réinitialisation). */
export const jetonOpaqueSchema = z
  .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .min(1, msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .max(LONGUEUR_MAX.JETON, msg(CodeErreur.ERREUR_VALIDATION));

/** Identifiant UUID. */
export const uuidSchema = z
  .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .regex(RE_UUID, msg(CodeErreur.ERREUR_VALIDATION));

/** Horodatage ISO 8601 UTC. */
export const isoDateTimeSchema = z
  .string(msg(CodeErreur.CHAMP_OBLIGATOIRE))
  .regex(RE_ISO_DATETIME_UTC, msg(CodeErreur.ERREUR_VALIDATION));

/** Heure `HH:mm` d'une plage de silence. */
export const heureHhMmSchema = z
  .string()
  .regex(RE_HEURE_HH_MM, msg(CodeErreur.ERREUR_VALIDATION));

/** Fuseau horaire IANA, contrôlé auprès de l'ICU embarquée. */
export const fuseauHoraireSchema = z
  .string()
  .transform((v) => v.trim())
  .refine(estFuseauHoraireValide, msg(CodeErreur.FUSEAU_HORAIRE_INVALIDE));

/** Langue de l'application. */
export const langueSchema = z.nativeEnum(Langue, msg(CodeErreur.ERREUR_VALIDATION));

/** Tension de gardiennage : [1 kV ; 12 kV], pas de 0,1 kV. */
export const tensionGardiennageSchema = z
  .number(msg(CodeErreur.ERREUR_VALIDATION))
  .min(BORNES.GARDIENNAGE_MIN_KV, msg(CodeErreur.VALEUR_HORS_BORNES))
  .max(BORNES.GARDIENNAGE_MAX_KV, msg(CodeErreur.VALEUR_HORS_BORNES))
  .refine((v) => respectePasGardiennage(v), msg(CodeErreur.VALEUR_NON_AUTORISEE));

/** Fréquence de remontée GPS : 5, 15 ou 30 minutes (arbitrage retenu). */
export const frequenceGeolocSchema = z.union(
  [z.literal(FREQUENCES_GEOLOC_MIN[0]), z.literal(FREQUENCES_GEOLOC_MIN[1]), z.literal(FREQUENCES_GEOLOC_MIN[2])],
  msg(CodeErreur.VALEUR_NON_AUTORISEE),
);

/** Fenêtre de l'historique de tension. */
export const periodeHistoriqueSchema = z.nativeEnum(
  PeriodeHistorique,
  msg(CodeErreur.FENETRE_NON_SUPPORTEE),
);

/** Taille de page d'une liste paginée. */
export const limitePageSchema = z.coerce
  .number()
  .int(msg(CodeErreur.ERREUR_VALIDATION))
  .min(BORNES.LIMITE_PAGE_MIN, msg(CodeErreur.VALEUR_HORS_BORNES))
  .max(BORNES.LIMITE_PAGE_MAX, msg(CodeErreur.VALEUR_HORS_BORNES));

/** Curseur opaque de pagination. */
export const curseurSchema = z.string().min(1).max(512);

/** Latitude WGS84. */
export const latitudeSchema = z.number().min(-90).max(90);

/** Longitude WGS84. */
export const longitudeSchema = z.number().min(-180).max(180);

/* -------------------------------------------------------------------------- */
/* 7. Schémas de requête — parcours de compte                                  */
/* -------------------------------------------------------------------------- */

/**
 * Contrôle « les deux saisies de mot de passe sont identiques ».
 * La confirmation est comparée APRÈS normalisation NFC, sinon deux saisies visuellement
 * identiques mais composées différemment seraient déclarées divergentes.
 */
function verifierConfirmation(
  motDePasse: string,
  confirmation: string | undefined,
  ctx: z.RefinementCtx,
  cheminConfirmation: string,
): void {
  if (confirmation !== undefined && normaliserNfc(confirmation) !== motDePasse) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [cheminConfirmation],
      message: CodeErreur.CONFIRMATION_MOT_DE_PASSE_INCORRECTE,
    });
  }
}

/** `POST /v1/exploitations` — écran 01B. */
export const creationExploitationRequeteSchema = z
  .object({
    jeton_appairage: jetonOpaqueSchema,
    nom_exploitation: nomExploitationSchema,
    prenom: prenomSchema,
    nom: nomSchema,
    email: emailSchema,
    mot_de_passe: motDePasseSchema,
    confirmation_mot_de_passe: z.string().optional(),
    langue: langueSchema.optional(),
    fuseau_horaire: fuseauHoraireSchema.optional(),
    rester_connecte: z.boolean().optional(),
    ignorer_exploitations_similaires: z.boolean().optional(),
  })
  .superRefine((valeurs, ctx) => {
    verifierConfirmation(
      valeurs.mot_de_passe,
      valeurs.confirmation_mot_de_passe,
      ctx,
      'confirmation_mot_de_passe',
    );
    const robustesse = verifierRobustesseMotDePasse(valeurs.mot_de_passe, {
      email: valeurs.email,
      prenom: valeurs.prenom,
      nom: valeurs.nom,
      nom_exploitation: valeurs.nom_exploitation,
    });
    if (!robustesse.valide) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mot_de_passe'],
        message: CodeErreur.MOT_DE_PASSE_TROP_FAIBLE,
      });
    }
  });

/** `POST /v1/users` — écran 01E (l'e-mail vient de l'invitation, il n'est pas transmis). */
export const creationCompteInviteRequeteSchema = z
  .object({
    jeton_invitation: jetonOpaqueSchema,
    prenom: prenomSchema,
    nom: nomSchema,
    mot_de_passe: motDePasseSchema,
    confirmation_mot_de_passe: z.string().optional(),
    langue: langueSchema.optional(),
    fuseau_horaire: fuseauHoraireSchema.optional(),
    rester_connecte: z.boolean().optional(),
  })
  .superRefine((valeurs, ctx) => {
    verifierConfirmation(
      valeurs.mot_de_passe,
      valeurs.confirmation_mot_de_passe,
      ctx,
      'confirmation_mot_de_passe',
    );
    const robustesse = verifierRobustesseMotDePasse(valeurs.mot_de_passe, {
      prenom: valeurs.prenom,
      nom: valeurs.nom,
    });
    if (!robustesse.valide) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mot_de_passe'],
        message: CodeErreur.MOT_DE_PASSE_TROP_FAIBLE,
      });
    }
  });

/** `POST /v1/auth/login` — écran 01F. */
export const connexionRequeteSchema = z.object({
  email: emailSchema,
  mot_de_passe: z.string().min(1, msg(CodeErreur.CHAMP_OBLIGATOIRE)),
  // Valeur usine « non coché » (docs/05, variable de contexte ResterConnecte) : le champ
  // est donc facultatif et vaut false par défaut. L'imposer ferait échouer toute connexion
  // d'un client qui ne coche pas la case.
  rester_connecte: z.boolean().default(false),
  jeton_push: z.string().max(LONGUEUR_MAX.JETON_FCM).optional(),
  fuseau_horaire: fuseauHoraireSchema.optional(),
});

/** `POST /v1/auth/refresh`. */
export const rafraichissementRequeteSchema = z.object({
  refresh_token: jetonOpaqueSchema,
});

/** `POST /v1/auth/logout`. */
export const deconnexionRequeteSchema = z.object({
  refresh_token: jetonOpaqueSchema,
  revoquer_toutes_les_sessions: z.boolean().optional(),
});

/** `POST /v1/auth/password/forgot` — réponse toujours 202 (anti-énumération). */
export const motDePasseOublieRequeteSchema = z.object({
  email: emailSchema,
});

/** `POST /v1/auth/password/reset`. */
export const reinitialisationMotDePasseRequeteSchema = z.object({
  token: jetonOpaqueSchema,
  nouveau_mot_de_passe: motDePasseSchema,
});

/** `POST /v1/me/password` — écran 05A (le mot de passe actuel est un ajout de sécurité). */
export const changementMotDePasseRequeteSchema = z
  .object({
    mot_de_passe_actuel: z.string().min(1, msg(CodeErreur.CHAMP_OBLIGATOIRE)),
    nouveau_mot_de_passe: motDePasseSchema,
    confirmation_nouveau_mot_de_passe: z.string().optional(),
  })
  .superRefine((valeurs, ctx) => {
    verifierConfirmation(
      valeurs.nouveau_mot_de_passe,
      valeurs.confirmation_nouveau_mot_de_passe,
      ctx,
      'confirmation_nouveau_mot_de_passe',
    );
    if (normaliserNfc(valeurs.mot_de_passe_actuel) === valeurs.nouveau_mot_de_passe) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nouveau_mot_de_passe'],
        message: CodeErreur.MOT_DE_PASSE_IDENTIQUE_A_ANCIEN,
      });
    }
  });

/** `PATCH /v1/me` — écrans 05A / 05A.1. */
export const miseAJourProfilRequeteSchema = z
  .object({
    prenom: prenomSchema.optional(),
    nom: nomSchema.optional(),
    langue: langueSchema.optional(),
    fuseau_horaire: fuseauHoraireSchema.optional(),
  })
  .refine(
    (valeurs) => Object.values(valeurs).some((v) => v !== undefined),
    msg(CodeErreur.CHAMP_OBLIGATOIRE),
  );

/** `PUT /v1/me/notification-preferences` — écran 05C (sauvegarde immédiate). */
export const miseAJourPreferencesNotificationRequeteSchema = z
  .object({
    push_actif: z.boolean().optional(),
    email_actif: z.boolean().optional(),
    criticite_minimale: z
      .nativeEnum(CriticiteMinimaleNotification, msg(CodeErreur.ERREUR_VALIDATION))
      .optional(),
    plage_silence_debut: heureHhMmSchema.nullable().optional(),
    plage_silence_fin: heureHhMmSchema.nullable().optional(),
  })
  .refine(
    (valeurs) => Object.values(valeurs).some((v) => v !== undefined),
    msg(CodeErreur.CHAMP_OBLIGATOIRE),
  );

/** `POST /v1/me/push-tokens`. */
export const enregistrementJetonPushRequeteSchema = z.object({
  jeton_fcm: z.string().min(1).max(LONGUEUR_MAX.JETON_FCM),
  plateforme: z.nativeEnum(PlateformePush, msg(CodeErreur.ERREUR_VALIDATION)),
  version_app: z.string().regex(RE_VERSION_APP, msg(CodeErreur.ERREUR_VALIDATION)),
  modele_telephone: z.string().max(80).optional(),
});

/* -------------------------------------------------------------------------- */
/* 8. Schémas de requête — invitations et transfert d'administrateur           */
/* -------------------------------------------------------------------------- */

/** `POST /v1/invitations/verify` — écran 01D. */
export const verificationInvitationRequeteSchema = z.object({
  code: codeInvitationSchema,
});

/** `POST /v1/invitations` — écran 05B.1 (le rôle est forcé à USER côté serveur). */
export const creationInvitationRequeteSchema = z.object({
  email: emailSchema,
});

/** `POST /v1/admin-transfers` — écran 05B.3. */
export const creationTransfertAdminRequeteSchema = z.object({
  destinataire_id: uuidSchema,
});

/** `POST /v1/admin-transfers/{id}/decline`. */
export const refusTransfertAdminRequeteSchema = z.object({
  motif: z.string().max(LONGUEUR_MAX.MOTIF).optional(),
});

/** `DELETE /v1/users/{id}` — confirmation obligatoire côté serveur. */
export const retraitUtilisateurRequeteSchema = z.object({
  confirmation: z.literal(true, msg(CodeErreur.CONFIRMATION_REQUISE)),
});

/* -------------------------------------------------------------------------- */
/* 9. Schémas de requête — appareils                                           */
/* -------------------------------------------------------------------------- */

/** `POST /v1/onboarding/device-lookup` — écrans 01A et 04A.4. */
export const rechercheAppareilStockRequeteSchema = z.object({
  numero_serie: numeroSerieSchema,
  cle_appairage: cleAppairageSchema.optional(),
  origine: z.nativeEnum(OrigineNumeroSerie, msg(CodeErreur.ERREUR_VALIDATION)),
});

/** `POST /v1/devices` — écrans 01C et 04A.4 (appairage + nommage en une opération). */
export const appairageAppareilRequeteSchema = z
  .object({
    jeton_appairage: jetonOpaqueSchema.optional(),
    numero_serie: numeroSerieSchema.optional(),
    cle_appairage: cleAppairageSchema.optional(),
    nom: nomElectrificateurSchema,
  })
  .refine(
    (valeurs) => valeurs.jeton_appairage !== undefined || valeurs.numero_serie !== undefined,
    msg(CodeErreur.CHAMP_OBLIGATOIRE),
  );

/** `PATCH /v1/devices/{id}` — écran 04A.2 (renommage, en-tête `If-Match` attendu). */
export const renommageAppareilRequeteSchema = z.object({
  nom: nomElectrificateurSchema,
});

/** `DELETE /v1/devices/{id}` — écran 04A.3. */
export const retraitAppareilRequeteSchema = z.object({
  confirmation: z.literal(true, msg(CodeErreur.CONFIRMATION_REQUISE)),
});

/** `PUT /v1/devices/{id}/settings` — écran 04C. */
export const miseAJourParametresAppareilRequeteSchema = z
  .object({
    tension_gardiennage_kv: tensionGardiennageSchema.optional(),
    frequence_geoloc_min: frequenceGeolocSchema.optional(),
    surveillance_mouvement: z.boolean().optional(),
  })
  .refine(
    (valeurs) => Object.values(valeurs).some((v) => v !== undefined),
    msg(CodeErreur.CHAMP_OBLIGATOIRE),
  );

/** `POST /v1/devices/{id}/commands` — écran 04B (en-tête `Idempotency-Key` obligatoire). */
export const creationCommandeRequeteSchema = z.object({
  type: z.enum(
    [TypeCommande.POWER_ON, TypeCommande.POWER_OFF, TypeCommande.START_MEASURE_SESSION],
    msg(CodeErreur.COMMANDE_NON_SUPPORTEE),
  ),
  parametres: z
    .object({
      duree_s: z.coerce
        .number()
        .int()
        .min(BORNES.DUREE_SESSION_MESURE_MIN_S, msg(CodeErreur.VALEUR_HORS_BORNES))
        .max(BORNES.DUREE_SESSION_MESURE_MAX_S, msg(CodeErreur.VALEUR_HORS_BORNES))
        .optional(),
      mesure_precise: z.boolean().optional(),
    })
    .optional(),
});

/**
 * `PUT /v1/devices/{id}/commands/{commande_id}/nom` — nom donné à une « Mesure précise » à la fin
 * de la session. TEMPORAIRE, retiré avec la fonction (campagne d'étalonnage d'octobre 2026).
 */
export const nomMesurePreciseRequeteSchema = z.object({
  nom: z
    .string()
    .trim()
    .min(1, msg(CodeErreur.CHAMP_OBLIGATOIRE))
    .max(80, msg(CodeErreur.VALEUR_HORS_BORNES)),
});

/** `GET /v1/devices` — écran 04A. */
export const listeAppareilsRequeteSchema = z.object({
  tri: z.nativeEnum(TriListeAppareils, msg(CodeErreur.ERREUR_VALIDATION)).optional(),
  curseur: curseurSchema.optional(),
  limite: limitePageSchema.optional(),
});

/** `GET /v1/devices/{id}/voltage-history` — écran 04E. */
export const historiqueTensionRequeteSchema = z.object({
  fenetre: periodeHistoriqueSchema.optional(),
  max_points: z.coerce
    .number()
    .int()
    .min(1, msg(CodeErreur.VALEUR_HORS_BORNES))
    .max(BORNES.NB_MAX_POINTS_GRAPHE, msg(CodeErreur.VALEUR_HORS_BORNES))
    .optional(),
});

/** `GET /v1/devices/{id}/positions` — écrans 04D et 03B. */
export const positionsRequeteSchema = z.object({
  mode: z.nativeEnum(ModePositionsDemande, msg(CodeErreur.ERREUR_VALIDATION)).optional(),
  limite: z.coerce
    .number()
    .int()
    .min(1, msg(CodeErreur.VALEUR_HORS_BORNES))
    .max(BORNES.NB_POINTS_GEOLOC, msg(CodeErreur.VALEUR_HORS_BORNES))
    .optional(),
  /**
   * Navigation dans le temps (écran 04D) : les points du parcours dont le numéro d'ordre est
   * STRICTEMENT inférieur à `avant`. Absent : les derniers points, comme avant.
   */
  avant: z.coerce.number().int().min(2, msg(CodeErreur.VALEUR_HORS_BORNES)).optional(),
});

/* -------------------------------------------------------------------------- */
/* 10. Schémas de requête — alertes                                            */
/* -------------------------------------------------------------------------- */

/** `GET /v1/alerts` — écran 03A. */
export const listeAlertesRequeteSchema = z.object({
  portee: z.nativeEnum(PorteeListeAlertes, msg(CodeErreur.ERREUR_VALIDATION)).optional(),
  electrificateur_id: uuidSchema.optional(),
  curseur: curseurSchema.optional(),
  limite: limitePageSchema.optional(),
});

/** `POST /v1/alerts/read-all`. */
export const marquageToutLuRequeteSchema = z.object({
  avant_at: isoDateTimeSchema.optional(),
  electrificateur_id: uuidSchema.optional(),
});

/** `DELETE /v1/alerts` — écran 03A.1 (purge GLOBALE à l'exploitation). */
export const purgeAlertesRequeteSchema = z.object({
  confirmation: z.literal(true, msg(CodeErreur.CONFIRMATION_REQUISE)),
  inclure_non_lues: z.boolean().optional(),
});

/* -------------------------------------------------------------------------- */
/* 11. Schémas d'ingestion (voie HTTPS de repli)                               */
/* -------------------------------------------------------------------------- */

const etatAlimentationSchema = z.nativeEnum(EtatAlimentation, msg(CodeErreur.SCHEMA_INVALIDE));
const etatAcquittementSchema = z.nativeEnum(EtatAcquittement, msg(CodeErreur.SCHEMA_INVALIDE));
const causeEchecSchema = z.nativeEnum(CauseEchecCommande, msg(CodeErreur.SCHEMA_INVALIDE));

/**
 * `up/state` : la v2 écrit `alim` et `fw`, la v1 `alimentation` et `firmware_version`.
 *
 * Ici le défaut était SILENCIEUX : `z.object` retire les clés inconnues au lieu de rejeter la
 * trame. L'état v2 était donc accepté, mais amputé de sa version de firmware et de son
 * alimentation — la base affichait « inconnue » pour une carte qui annonçait « 2.0.0 » à chaque
 * connexion, et le suivi d'une mise à jour à distance n'avait rien sur quoi s'appuyer.
 */
const RENOMMAGES_ETAT_V2 = { alim: 'alimentation', fw: 'firmware_version' };

const donneesEtatSchema = z.preprocess(
  (brut) => versFormeInterne(brut, RENOMMAGES_ETAT_V2),
  z.object({
    online: z.boolean(),
    raison: z.enum(['CONNEXION', 'DECONNEXION', 'LWT']),
    alimentation: etatAlimentationSchema.optional(),
    firmware_version: z.string().max(LONGUEUR_MAX.FIRMWARE_VERSION).optional(),
  }),
);

const donneesHeartbeatSchema = z.object({
  rssi_dbm: z.number().min(-150).max(0),
  firmware_version: z.string().max(LONGUEUR_MAX.FIRMWARE_VERSION),
  uptime_s: z.number().int().min(0),
  batterie_v: z.number().min(0).max(60).optional(),
  batterie_secours_v: z.number().min(0).max(60).optional(),
  batterie_pct: z.number().min(0).max(100).optional(),
  batterie_secours_pct: z.number().min(0).max(100).optional(),
  alimentation: etatAlimentationSchema,
  cfg_version: z.number().int().min(0).optional(),
  reseau: z.string().max(40).optional(),
});

/**
 * Télémétrie, PROTOCOLE 1 : une mesure par trame.
 *
 * Conservée telle quelle. Le parc de démonstration et tout module qui n'a pas encore reçu le
 * firmware 2.x publient sous cette forme, et casser cette voie couperait la démonstration
 * entière.
 */
const donneesTelemetrieV1Schema = z.object({
  tension_kv: z.number().min(0).max(20),
  nb_impulsions: z.number().int().min(1).max(255),
  tension_min_kv: z.number().min(0).max(20).optional(),
  tension_max_kv: z.number().min(0).max(20).optional(),
  alimentation: etatAlimentationSchema,
  batterie_v: z.number().min(0).max(60).optional(),
  session_id: uuidSchema.optional(),
});

/** Un point de mesure du protocole 2 : un slot de 20 s résumé en quatre nombres. */
const pointMesureSchema = z.object({
  /** Décalage en SECONDES depuis l'horodatage de la trame. */
  o: z.number().int().min(0).max(86400),
  /** Tension moyenne des impulsions présentes, en kV. */
  v: z.number().min(0).max(20),
  /** Nombre d'impulsions sous le seuil de gardiennage dans le slot. */
  s: z.number().int().min(0).max(255),
  /** Nombre d'impulsions absentes dans le slot (tension mesurée sous 100 V). */
  a: z.number().int().min(0).max(255),
});

/**
 * Position du protocole 2 — TROIS états, et ils ne disent pas la même chose.
 *
 *   {la, lo, g}  position complète
 *   0            INCHANGÉE : je sais où je suis, c'est le même endroit qu'au dernier envoi
 *   null/absent  AUCUN FIX : le GNSS n'a rien donné ce cycle
 *
 * La première version n'avait que deux états, et disait d'envoyer `0` dans les deux derniers
 * cas. C'était une faute : « je suis toujours là » et « je ne sais pas où je suis » sont
 * exactement ce que le drapeau devait permettre de distinguer — c'est même la raison pour
 * laquelle on ne se contente pas du silence (D-12). Avec une seule valeur, le backend ne peut
 * pas dire si un boîtier est immobile ou si son récepteur est mort.
 */
const positionGroupeeSchema = z.union([
  z.literal(0),
  z.null(),
  z.object({
    la: latitudeSchema,
    lo: longitudeSchema,
    /** Nombre de fixes GNSS réellement retenus pour la consolidation. */
    g: z.number().int().min(0).max(64).optional(),
  }),
]);

/** État du module, transporté par la trame groupée au lieu d'un canal `heartbeat` séparé. */
const etatModuleGroupeSchema = z.object({
  rssi: z.number().int().min(-140).max(0).optional(),
  /** Uptime en secondes. */
  up: z.number().int().min(0).optional(),
  /** Tension batterie en DIXIÈMES de volt — 127 vaut 12,7 V. */
  bat: z.number().int().min(0).max(600).optional(),
  alim: etatAlimentationSchema.optional(),
  /** Version de configuration réellement appliquée. */
  cfg: z.number().int().min(0).optional(),
  fw: z.string().max(LONGUEUR_MAX.FIRMWARE_VERSION).optional(),
  reseau: z.string().max(40).optional(),
  /** Diagnostic GNSS du dernier cycle (firmware ≥ 2.4.1), voir `DiagnosticGnss`. */
  gn: z
    .object({
      f: z.number().int().min(0).optional(),
      h: z.number().int().min(0).optional(),
      s: z.number().int().min(0).optional(),
      a: z.number().int().min(-1).optional(),
      c: z.number().int().min(0).max(1).optional(),
      v: z.number().int().min(-1).optional(),
      t: z.number().int().min(0).optional(),
      cn: z.number().int().min(0).optional(),
    })
    .optional(),
});

/**
 * Télémétrie, PROTOCOLE 2 : une trame porte plusieurs points, la position et l'état du module.
 *
 * C'est le format arbitré par D-12. Il divise la consommation par sept — 135 Mo sur sept ans
 * contre 1027 — parce que l'enveloppe et le transport sont payés une fois pour cinq mesures au
 * lieu d'une fois par mesure.
 */
const donneesTelemetrieV2Schema = z.object({
  p: z.array(pointMesureSchema).min(1).max(30),

  // POSITION COMPLÈTE, À PLAT dans `data` : c'est la forme documentée dans le prompt firmware
  // (« "la":44.6832,"lo":2.8487,"g":8 ») et celle que produit la première carte réelle.
  la: latitudeSchema.optional(),
  lo: longitudeSchema.optional(),
  /** Nombre de fixes GNSS retenus pour la consolidation. */
  g: z.number().int().min(0).max(64).optional(),
  /** Dilution horizontale de précision. Convertie en mètres à l'ingestion. */
  hdop: z.number().min(0).max(100).optional(),

  // `pos` porte le DRAPEAU (`0` inchangée, `null` aucun fix). La forme objet est acceptée
  // en plus, parce que l'exemple de trame du prompt la montrait : un firmware écrit d'après
  // l'exemple plutôt que d'après le tableau ne doit pas être puni pour autant.
  pos: positionGroupeeSchema.optional(),

  hb: etatModuleGroupeSchema.optional(),
  session_id: uuidSchema.optional(),
});

/**
 * Les deux formes cohabitent, et il faut qu'elles cohabitent : la première carte réelle publie
 * en protocole 2 depuis le 21 septembre, le parc de démonstration en protocole 1. La
 * discrimination se fait sur la présence de `p`, jamais sur le champ `v` de l'enveloppe — un
 * firmware qui oublierait de l'incrémenter ne doit pas faire rejeter ses trames.
 */
const donneesTelemetrieSchema = z.union([donneesTelemetrieV2Schema, donneesTelemetrieV1Schema]);

const donneesPositionSchema = z.object({
  lat: latitudeSchema,
  lon: longitudeSchema,
  precision_m: z.number().min(0).max(10000).optional(),
  en_mouvement: z.boolean(),
  satellites: z.number().int().min(0).max(64).optional(),
  vitesse_ms: z.number().min(0).max(200).optional(),
});

/**
 * Protocole 2 → forme interne, pour les évènements et les acquittements.
 *
 * LE BACKEND REJETAIT LE PROTOCOLE 2 SUR CES DEUX CANAUX. La télémétrie groupée avait été
 * prise en charge le 21 septembre ; `up/event` et `up/ack` étaient restés sur les noms v1. Relevé
 * dans le journal d'ingestion le 24 : 287 évènements et 36 acquittements de la carte 1000000115
 * refusés en `SCHEMA_INVALIDE`, dont tous ses `APPLIED` — « data.etat_appareil.alimentation ».
 * La carte confirmait bien ses ordres ; c'est nous qui jetions la confirmation.
 *
 *   évènement v2   {"e":…,"la":…,"lo":…,"cmd_id":null,"ctx":{…}}
 *   acquittement   {"cmd_id":…,"etat":…,"cause":…,"etat_appareil":{"alim":…,"v":…},"cfg":…}
 *
 * Les noms v2 sont traduits vers les noms v1 AVANT validation, et seulement quand le nom v1 est
 * absent : une carte v1 est traitée exactement comme avant, et les traitements en aval n'ont
 * qu'une forme à connaître. `cmd_id: null` — la v2 l'émet explicitement pour un évènement
 * spontané — devient « absent ».
 */
function versFormeInterne(brut: unknown, renommages: Record<string, string>): unknown {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return brut;
  const entree = brut as Record<string, unknown>;
  const sortie: Record<string, unknown> = { ...entree };
  for (const [v2, v1] of Object.entries(renommages)) {
    if (v2 in sortie) {
      if (!(v1 in sortie)) sortie[v1] = sortie[v2];
      delete sortie[v2];
    }
  }
  if (sortie['cmd_id'] === null) delete sortie['cmd_id'];
  return sortie;
}

const RENOMMAGES_EVENEMENT_V2 = { e: 'evenement', la: 'lat', lo: 'lon', ctx: 'contexte' };
const RENOMMAGES_ACQUITTEMENT_V2 = { cause: 'cause_echec', cfg: 'cfg_version' };
const RENOMMAGES_ETAT_APPAREIL_V2 = { alim: 'alimentation', v: 'tension_kv' };

const donneesEvenementSchema = z.preprocess(
  (brut) => versFormeInterne(brut, RENOMMAGES_EVENEMENT_V2),
  z.object({
  evenement: z.nativeEnum(TypeEvenementObjet, msg(CodeErreur.SCHEMA_INVALIDE)),
  lat: latitudeSchema.optional(),
  lon: longitudeSchema.optional(),
  cmd_id: uuidSchema.optional(),
  // `null` accepté : le protocole 2 met `ctx` à null quand il n'a rien à dire.
  contexte: z.record(z.union([z.number(), z.string(), z.boolean(), z.null()])).optional(),
  }),
);

const donneesAcquittementSchema = z.preprocess(
  (brut) => {
    const interne = versFormeInterne(brut, RENOMMAGES_ACQUITTEMENT_V2) as Record<string, unknown>;
    if (typeof interne === 'object' && interne !== null && 'etat_appareil' in interne) {
      interne['etat_appareil'] = versFormeInterne(interne['etat_appareil'], RENOMMAGES_ETAT_APPAREIL_V2);
    }
    return interne;
  },
  z.object({
    cmd_id: uuidSchema,
    etat: etatAcquittementSchema,
    cause_echec: causeEchecSchema.optional(),
    etat_appareil: z
      .object({
        alimentation: etatAlimentationSchema,
        tension_kv: z.number().min(0).max(20).optional(),
      })
      .optional(),
    cfg_version: z.number().int().min(0).optional(),
  }),
);

const donneesBootSchema = z.object({
  firmware_version: z.string().max(LONGUEUR_MAX.FIRMWARE_VERSION),
  cause: z.enum(['POWER_ON', 'RESET', 'WATCHDOG', 'OTA', 'INCONNUE']),
  cfg_version: z.number().int().min(0).optional(),
});

const donneesDiagnosticSchema = z.object({
  metriques: z.record(z.union([z.number(), z.string(), z.boolean()])),
});

/** Trame montante : enveloppe d'ingestion + charge utile discriminée par `type`. */
export const trameMontanteSchema = z
  .discriminatedUnion(
    'type',
    [
      z.object({ type: z.literal(TypeMessageMontant.STATE), data: donneesEtatSchema }),
      z.object({ type: z.literal(TypeMessageMontant.HEARTBEAT), data: donneesHeartbeatSchema }),
      z.object({ type: z.literal(TypeMessageMontant.MEASURE), data: donneesTelemetrieSchema }),
      z.object({ type: z.literal(TypeMessageMontant.POSITION), data: donneesPositionSchema }),
      z.object({ type: z.literal(TypeMessageMontant.EVENT), data: donneesEvenementSchema }),
      z.object({ type: z.literal(TypeMessageMontant.ACK), data: donneesAcquittementSchema }),
      z.object({ type: z.literal(TypeMessageMontant.BOOT), data: donneesBootSchema }),
      z.object({ type: z.literal(TypeMessageMontant.DIAG), data: donneesDiagnosticSchema }),
    ],
    msg(CodeErreur.SCHEMA_INVALIDE),
  )
  .and(
    z.object({
      message_id: z.string().min(1).max(36),
      t: isoDateTimeSchema,
    }),
  );

/**
 * `POST /ingest/v1/uplink`. `device_sn` est purement informatif : l'identité de l'objet vient du
 * certificat client. Une valeur divergente est rejetée en 403 (IDENTITE_OBJET_DIVERGENTE), le
 * schéma ne peut pas le contrôler seul.
 */
export const uplinkRequeteSchema = z.object({
  device_sn: z.string().max(LONGUEUR_MAX.NUMERO_SERIE).optional(),
  boot_count: z.number().int().min(0),
  seq: z.number().int().min(0),
  messages: z.array(trameMontanteSchema).min(1).max(200),
});

/** `POST /ingest/v1/commands/{id}/ack`. */
export const acquittementCommandeRequeteSchema = z.object({
  etat: etatAcquittementSchema,
  cause_echec: causeEchecSchema.optional(),
  etat_appareil: z
    .object({
      alimentation: etatAlimentationSchema,
      tension_kv: z.number().min(0).max(20).optional(),
    })
    .optional(),
});

/* -------------------------------------------------------------------------- */
/* 12. Types inférés                                                           */
/* -------------------------------------------------------------------------- */

export type CreationExploitationRequeteValidee = z.infer<
  typeof creationExploitationRequeteSchema
>;
export type CreationCompteInviteRequeteValidee = z.infer<
  typeof creationCompteInviteRequeteSchema
>;
export type ConnexionRequeteValidee = z.infer<typeof connexionRequeteSchema>;
export type AppairageAppareilRequeteValidee = z.infer<typeof appairageAppareilRequeteSchema>;
export type MiseAJourParametresAppareilRequeteValidee = z.infer<
  typeof miseAJourParametresAppareilRequeteSchema
>;
export type CreationCommandeRequeteValidee = z.infer<typeof creationCommandeRequeteSchema>;
export type NomMesurePreciseRequete = z.infer<typeof nomMesurePreciseRequeteSchema>;
export type UplinkRequeteValidee = z.infer<typeof uplinkRequeteSchema>;
export type TrameMontanteValidee = z.infer<typeof trameMontanteSchema>;
